import type { components } from "@contracts/generated/editor-service";

import { EVENT_KIND_LABELS, type TraceEventKind } from "./previewDecisions";

/**
 * Preview Insights — reading the server's two-run comparison (#1107, approved
 * design §11.3).
 *
 * `POST /v1/projects/{projectId}/preview-comparisons` (#1106) answers a
 * DESCRIPTIVE comparison of two authorized runs: aligned pairs with the
 * recorded facts that differ, events only one run holds, typed reasons why an
 * unmatched event is not reported as an absence, and every way the experiment
 * was not controlled. This module turns that answer into the shape the Compare
 * surface renders, and does nothing else.
 *
 * ## What this module deliberately does not do
 *
 * It computes NO difference. Which facts differ, which events aligned, which
 * are added, removed or unalignable, whether the experiment was controlled —
 * every one of those is the server's finding, arriving as a typed member. The
 * functions below group, label and count what came back; none of them compares
 * two values, and none of them decides what a difference MEANS. The ticket's
 * boundary is exact: no clinical-difference semantics in the browser, no causal
 * inference, and no winning candidate — and the server's own vocabulary carries
 * no winner or score for one to be derived from.
 *
 * `controlOf` is the sharpest case. "Controlled" is not a judgement this module
 * makes from the identity axes: it is `confounders.length === 0` — the server's
 * own statement that it found no uncontrolled axis — and any confounder at all
 * makes the result confounded. A browser that re-derived control from the axes
 * could disagree with the service that computed them, and the design (§11.3)
 * says the label follows the service.
 */

export type PreviewRunComparison =
  components["schemas"]["PreviewRunComparison"];
export type PreviewRunComparisonSide =
  components["schemas"]["PreviewRunComparisonSide"];
export type PreviewRunComparisonResult =
  components["schemas"]["PreviewRunComparisonResult"];
export type PreviewRunComparisonSideName =
  components["schemas"]["PreviewRunComparisonSideName"];
export type PreviewRunComparisonField =
  components["schemas"]["PreviewRunComparisonField"];
export type PreviewRunConfounder =
  components["schemas"]["PreviewRunConfounder"];
export type PreviewRunConfounderKind =
  components["schemas"]["PreviewRunConfounderKind"];
export type PreviewRunConfounderScope =
  components["schemas"]["PreviewRunConfounderScope"];
export type PreviewRunAlignedEvent =
  components["schemas"]["PreviewRunAlignedEvent"];
export type PreviewRunAddedEvent = components["schemas"]["PreviewRunAddedEvent"];
export type PreviewRunRemovedEvent =
  components["schemas"]["PreviewRunRemovedEvent"];
export type PreviewRunUnalignableEvent =
  components["schemas"]["PreviewRunUnalignableEvent"];
export type PreviewRunUnalignableReason =
  components["schemas"]["PreviewRunUnalignableReason"];
export type PreviewRunAlignmentKey =
  components["schemas"]["PreviewRunAlignmentKey"];
export type PreviewRunEventDifference =
  components["schemas"]["PreviewRunEventDifference"];
export type PreviewRunIdentityComparison =
  components["schemas"]["PreviewRunIdentityComparison"];
export type PreviewRunIdentityAxis =
  components["schemas"]["PreviewRunIdentityAxis"];
export type PreviewRunInputScriptRelation =
  components["schemas"]["PreviewRunInputScriptRelation"];
export type PreviewRunLayerComparison =
  components["schemas"]["PreviewRunLayerComparison"];
export type PreviewRunSegmentComparison =
  components["schemas"]["PreviewRunSegmentComparison"];
export type PreviewRunCallTypeCounts =
  components["schemas"]["PreviewRunCallTypeCounts"];
export type PreviewRunFamilyCounts =
  components["schemas"]["PreviewRunFamilyCounts"];
export type PreviewRunComparisonCounts =
  components["schemas"]["PreviewRunComparisonCounts"];

/** The request body: two opaque locators, and — the contract is explicit — nothing else. */
export type ComparePreviewRunsRequest =
  components["schemas"]["ComparePreviewRunsRequest"];

// ── The two runs a clinician names ───────────────────────────────────────────

/**
 * Why a locator cannot be sent, decided WITHOUT asking the server.
 *
 * Exactly two, and deliberately no third. An empty box is not a request, and a
 * run compared with itself is refused 422 by the service itself — both are
 * facts the browser already holds about what the clinician typed, so asking
 * would spend two upstream run reads to be told what is on screen.
 *
 * Nothing here inspects the SHAPE of a locator. `ComparePreviewRunsRequest`
 * publishes both members as deliberately unbounded strings, because a bound
 * would answer one class for an overlong value and another for an unknown one —
 * a second observable class keyed on the locator's shape, which ADR 0013 N2
 * rules out. A browser-side pattern would rebuild exactly that: a locator this
 * build's regular expression does not recognise would be refused here as
 * malformed while the service would have answered its one generic not-found.
 * Every judgement about whether a locator names anything is the resolver's.
 */
export type ComparandRefusal = "empty" | "same_run";

export const COMPARAND_REFUSAL_SENTENCES = {
  empty: "Paste the other run's locator to compare it with this one.",
  same_run:
    "This is the run you are already looking at. A run cannot be compared with itself — open the other run's page and copy its locator from the address.",
} as const satisfies Record<ComparandRefusal, string>;

/**
 * What the clinician typed, judged against the run this page is showing.
 *
 * Trimmed, because a pasted address line carries surrounding whitespace and a
 * locator does not contain any; the trimmed value is what would be sent.
 */
export function readComparand(
  typed: string,
  currentPreviewRunRef: string,
): { ref: string; refusal: ComparandRefusal | null } {
  const ref = typed.trim();
  if (ref.length === 0) {
    return { ref, refusal: "empty" };
  }
  if (ref === currentPreviewRunRef) {
    return { ref, refusal: "same_run" };
  }
  return { ref, refusal: null };
}

/**
 * Which of the two runs is read FROM.
 *
 * The direction is not a preference and the contract says so: it exists so that
 * `added` and `removed` have a meaning. The page's own run is the base by
 * default — the reading is "what does the other run do that this one does not" —
 * and the clinician can swap the two, which re-asks the server rather than
 * re-labelling the answer, because `added`/`removed` and every `base` value are
 * computed against the direction that was sent.
 */
export type CompareDirection = "this_run_is_base" | "this_run_is_compared";

export function compareRequestBody(
  thisRunRef: string,
  otherRunRef: string,
  direction: CompareDirection,
): ComparePreviewRunsRequest {
  return direction === "this_run_is_base"
    ? { basePreviewRunRef: thisRunRef, comparedPreviewRunRef: otherRunRef }
    : { basePreviewRunRef: otherRunRef, comparedPreviewRunRef: thisRunRef };
}

// ── The reading ──────────────────────────────────────────────────────────────

/**
 * Both runs resolved; whether they could be compared is the second question.
 *
 * `comparison` is null exactly when one run's `contentAvailable` is false, and
 * the contract is emphatic that this is a 200 and not a not-found: the caller
 * was authorized, the binding matched, and the answer is that this particular
 * run holds nothing this deployment can read. The two headers say which side
 * and why, so the reading keeps them rather than collapsing onto "no result".
 */
export type ComparisonReading =
  | {
      kind: "compared";
      base: PreviewRunComparisonSide;
      compared: PreviewRunComparisonSide;
      result: PreviewRunComparisonResult;
    }
  | {
      kind: "unreadable";
      base: PreviewRunComparisonSide;
      compared: PreviewRunComparisonSide;
      /** The sides whose content is unavailable — one or both, in contract order. */
      unreadable: readonly {
        side: PreviewRunComparisonSideName;
        run: PreviewRunComparisonSide;
      }[];
    };

export function readComparison(
  response: PreviewRunComparison,
): ComparisonReading {
  const { base, compared, comparison } = response;
  if (comparison !== null && comparison !== undefined) {
    return { kind: "compared", base, compared, result: comparison };
  }
  const unreadable = (
    [
      { side: "base", run: base },
      { side: "compared", run: compared },
    ] as const
  ).filter(({ run }) => !run.run.contentAvailable);
  return { kind: "unreadable", base, compared, unreadable };
}

// ── Control, and the confounders that decide it ──────────────────────────────

/**
 * The one label the design names, and the only thing derived from the whole
 * result: `controlled` when the service reported no uncontrolled axis,
 * `confounded` when it reported any.
 *
 * Read off `confounders` and nothing else. In particular NOT off `identity`:
 * `candidate_content_hash` is the VARIABLE of an A/B experiment and differs on
 * purpose in the comparison this feature exists for, so a label derived from
 * "are all six axes the same" would call every real experiment confounded. The
 * service already knows which axes are controls and emits a confounder for each
 * one that moved.
 */
export type ControlLabel = "controlled" | "confounded";

export function controlOf(result: PreviewRunComparisonResult): ControlLabel {
  return result.confounders.length === 0 ? "controlled" : "confounded";
}

export const CONTROL_LABELS = {
  controlled: "Controlled",
  confounded: "Confounded",
} as const satisfies Record<ControlLabel, string>;

export const CONTROL_TONES = {
  controlled: "success",
  confounded: "warning",
} as const satisfies Record<ControlLabel, "success" | "warning">;

/**
 * What each label states — and, as carefully, what it does not.
 *
 * "Controlled" is scoped to what the service can see, in the service's own
 * words, because a clinician must not read it as "nothing else changed". Even
 * a controlled comparison asserts no cause: the disclaimer the result carries
 * is the server's fixed sentence and is rendered verbatim beside these.
 */
export const CONTROL_SENTENCES = {
  controlled:
    "The Editor Service found no uncontrolled axis it can see: the input script, base pack, serving Brain, model, preview target and capture completeness all match. It still asserts no cause for any difference below.",
  confounded:
    "The two runs differ on something other than the candidate. Every difference below is reported in full, but something besides the candidate could account for it.",
} as const satisfies Record<ControlLabel, string>;

export const CONFOUNDER_LABELS = {
  input_script: "Different synthetic input script",
  base_pack: "Different base pack",
  brain_commit: "Different serving Brain commit",
  brain_image: "Different serving Brain image",
  model: "Different model",
  model_deployment: "Different model deployment",
  preview_target: "Different preview target",
  preview_target_revision: "Different preview-target revision",
  capture_incomplete: "A capture is not complete",
} as const satisfies Record<PreviewRunConfounderKind, string>;

/** One sentence per kind, stating the fact and why it matters to a comparison. */
export const CONFOUNDER_SENTENCES = {
  input_script:
    "The two runs were given different synthetic inputs, so a difference in what Liv said may follow from what it was asked.",
  base_pack:
    "The two candidates were composed on different base packs, so inherited clinical content may differ as well as authored content.",
  brain_commit:
    "The two runs were served by different Brain commits, so runtime behaviour may differ independently of the candidate.",
  brain_image:
    "The two runs were served by different Brain images, so runtime behaviour may differ independently of the candidate.",
  model:
    "The two runs were answered by different models, so wording and reasoning may differ independently of the candidate.",
  model_deployment:
    "The two runs were answered by different model deployments, so wording and reasoning may differ independently of the candidate.",
  preview_target:
    "The two runs were served by different preview targets. No target is named here: the fact that they differ is the whole of the finding.",
  preview_target_revision:
    "The two runs were dispatched under different preview-target revisions, so the Project's target moved between them.",
  capture_incomplete:
    "At least one run's trace is still being written or holds declared gaps, so an event missing on that side cannot be read as something that did not happen.",
} as const satisfies Record<PreviewRunConfounderKind, string>;

export const CONFOUNDER_SCOPE_LABELS = {
  run: "Recorded on the run",
  prompt_call: "Recorded on aligned prompt calls",
} as const satisfies Record<PreviewRunConfounderScope, string>;

/**
 * The literal a serving pod records for an identity axis it cannot attest.
 *
 * Rendered as its own sentence rather than as a value, for the reason the
 * contract gives: `unknown` never counts as equal to anything, including
 * another `unknown`, because an unverifiable control is not a control.
 */
export const UNATTESTED = "unknown";

export const IDENTITY_AXIS_LABELS = {
  candidate_content_hash: "Candidate content hash",
  base_pack_sha: "Base pack",
  serving_brain_commit: "Serving Brain commit",
  serving_image_digest: "Serving image digest",
  model: "Model",
  model_deployment: "Model deployment",
} as const satisfies Record<PreviewRunIdentityAxis, string>;

/**
 * The one axis that is the experiment's VARIABLE rather than one of its
 * controls — named here so the identity table can say which is which without
 * the browser deciding anything about the rest.
 */
export const CANDIDATE_AXIS: PreviewRunIdentityAxis = "candidate_content_hash";

export const INPUT_SCRIPT_RELATION_LABELS = {
  identical: "Identical",
  base_extends_compared: "The base run ran further",
  compared_extends_base: "The compared run ran further",
  diverges: "The scripts diverge",
} as const satisfies Record<PreviewRunInputScriptRelation, string>;

export const INPUT_SCRIPT_RELATION_SENTENCES = {
  identical:
    "Both runs were given the same synthetic inputs, in the same order.",
  base_extends_compared:
    "The base run was given every input the compared run was given, and then more.",
  compared_extends_base:
    "The compared run was given every input the base run was given, and then more.",
  diverges:
    "The two runs were given different synthetic inputs, so what Liv was asked is not held constant.",
} as const satisfies Record<PreviewRunInputScriptRelation, string>;

// ── Groups ───────────────────────────────────────────────────────────────────

/**
 * The comparison's groups, in the order the ticket names them.
 *
 * The first four are event families; `summary` and `identity` are not. A run's
 * clinical summary is not its own event family — it is a prompt call whose
 * `callType` says so, which is why the contract's per-call-type counts carry
 * the design's "summary output when available". `identity` is the run envelope's
 * six attested axes and this service's own dispatch record, which belong to the
 * runs rather than to any event.
 */
export const COMPARE_GROUPS = [
  "conversation",
  "prompts",
  "decisions",
  "safety",
  "summary",
  "identity",
] as const;
export type CompareGroupId = (typeof COMPARE_GROUPS)[number];

export const COMPARE_GROUP_LABELS = {
  conversation: "Conversation",
  prompts: "Prompts",
  decisions: "Decisions",
  safety: "Safety",
  summary: "Summary",
  identity: "Runtime identity",
} as const satisfies Record<CompareGroupId, string>;

/**
 * Which event families each group owns. Every one of the producer's nine
 * families belongs to exactly one group, so no event can be silently dropped —
 * `previewComparison.test.ts` asserts the partition against the contract's own
 * union rather than against this list.
 */
export const GROUP_KINDS = {
  conversation: ["turn", "runtime_transition"],
  prompts: ["prompt_call", "prompt_provenance"],
  decisions: ["classification", "criterion_threshold", "routing", "why_not"],
  safety: ["safety"],
  summary: [],
  identity: [],
} as const satisfies Record<CompareGroupId, readonly TraceEventKind[]>;

/** The group an event family is rendered under, or null for a family no group claims. */
export function groupOfKind(kind: TraceEventKind): CompareGroupId | null {
  for (const group of COMPARE_GROUPS) {
    if ((GROUP_KINDS[group] as readonly TraceEventKind[]).includes(kind)) {
      return group;
    }
  }
  return null;
}

export { EVENT_KIND_LABELS };

/**
 * One group's slice of the alignment, filtered on the alignment key's `kind`
 * and nothing else.
 *
 * The order each list arrives in is the contract's and is preserved: aligned
 * and removed in base-sequence order, added in compared-sequence order,
 * unalignable base-side first. Nothing is re-sorted — a re-sort would be this
 * browser imposing an order on events the producer already ordered.
 */
export type GroupAlignment = {
  aligned: readonly PreviewRunAlignedEvent[];
  added: readonly PreviewRunAddedEvent[];
  removed: readonly PreviewRunRemovedEvent[];
  unalignable: readonly PreviewRunUnalignableEvent[];
  /** The per-family rows the service computed, for the families in this group. */
  families: readonly PreviewRunFamilyCounts[];
};

export function groupAlignment(
  result: PreviewRunComparisonResult,
  group: CompareGroupId,
): GroupAlignment {
  const kinds = new Set<string>(GROUP_KINDS[group] as readonly string[]);
  const events = result.events;
  return {
    aligned: events.aligned.filter((item) => kinds.has(item.key.kind)),
    added: events.added.filter((item) => kinds.has(item.key.kind)),
    removed: events.removed.filter((item) => kinds.has(item.key.kind)),
    unalignable: events.unalignable.filter(
      (item) => item.key !== null && kinds.has(item.key.kind),
    ),
    families: result.families.filter((row) => kinds.has(row.kind)),
  };
}

/**
 * The unalignable events no group can claim: the contract admits a null key
 * (`missing_turn_number` leaves nothing to key on), and an event with no key
 * has no family to file it under. They are reported once, on the group tab that
 * owns the whole comparison's roll-up, rather than being dropped — ADR 0013 N7
 * forbids letting an event this service could not place read as one that did
 * not happen.
 */
export function unkeyedUnalignable(
  result: PreviewRunComparisonResult,
): readonly PreviewRunUnalignableEvent[] {
  return result.events.unalignable.filter((item) => item.key === null);
}

/** Whether a group has anything at all to show, so a tab can say "nothing here" truthfully. */
export function groupIsEmpty(alignment: GroupAlignment): boolean {
  return (
    alignment.aligned.length === 0 &&
    alignment.added.length === 0 &&
    alignment.removed.length === 0 &&
    alignment.unalignable.length === 0
  );
}

/**
 * The prompt-call rows a run's clinical summary lives in.
 *
 * The contract says where to look — a run that produced a clinical summary
 * holds prompt calls of that type — but not which spellings a producer uses, so
 * this is a rendering decision rather than a claim about the vocabulary: a call
 * type whose name contains "summary" is shown in the Summary group, and every
 * row is shown in the Prompts group regardless, so nothing can be hidden by a
 * spelling this build does not recognise.
 */
export function summaryCallTypes(
  result: PreviewRunComparisonResult,
): readonly PreviewRunCallTypeCounts[] {
  return result.promptCalls.filter((row) =>
    row.callType.toLowerCase().includes("summary"),
  );
}

/**
 * The EVENTS behind those rows, so the Summary group shows the summary's own
 * differences and not only how many there were (#1128 review, finding 3).
 *
 * A summary is a prompt call, so the slice is every aligned, added, removed or
 * unalignable event whose coordinate names one of the recognised call types —
 * the call itself and the provenance event citing it, which is the contract's
 * own rule for which events carry a `callType`. The counts table stays beside
 * it: a run whose summary was recorded under a spelling this build does not
 * recognise is visible there, with a notice saying so, rather than silently
 * absent from both.
 */
export function summaryAlignment(
  result: PreviewRunComparisonResult,
): GroupAlignment {
  const types = new Set(summaryCallTypes(result).map((row) => row.callType));
  const named = (key: PreviewRunAlignmentKey | null) =>
    key !== null &&
    (key.kind === "prompt_call" || key.kind === "prompt_provenance") &&
    key.callType !== null &&
    key.callType !== undefined
      ? types.has(key.callType)
      : false;
  const events = result.events;
  return {
    aligned: events.aligned.filter((item) => named(item.key)),
    added: events.added.filter((item) => named(item.key)),
    removed: events.removed.filter((item) => named(item.key)),
    unalignable: events.unalignable.filter((item) => named(item.key)),
    // The per-family rows are the wrong roll-up for a call-type slice; the
    // Summary group renders `promptCalls` rows instead, which is where the
    // contract puts a summary's counts.
    families: [],
  };
}

// ── Outcome cards ────────────────────────────────────────────────────────────

/**
 * What one card states about one coordinate. Four outcomes, and each is the
 * server's own: an aligned pair is `changed` or `identical` by its `outcome`
 * member, an event only one run holds is `added` or `removed` by the list it
 * arrived in.
 */
export type OutcomeKind = "changed" | "identical" | "added" | "removed";

export const OUTCOME_LABELS = {
  changed: "Changed",
  identical: "Identical",
  added: "Only in the compared run",
  removed: "Only in the base run",
} as const satisfies Record<OutcomeKind, string>;

export const OUTCOME_TONES = {
  changed: "warning",
  identical: "outline",
  added: "accent",
  removed: "accent",
} as const satisfies Record<
  OutcomeKind,
  "warning" | "outline" | "accent" | "neutral"
>;

export const ORDERING_LABELS = {
  in_order: "In the same order",
  reordered: "Reordered",
} as const satisfies Record<
  components["schemas"]["PreviewRunEventOrdering"],
  string
>;

export const UNALIGNABLE_REASON_LABELS = {
  missing_turn_number: "No turn number recorded",
  outside_shared_script: "Outside the shared script",
  counterpart_capture_gap: "The other run's capture does not cover this turn",
} as const satisfies Record<PreviewRunUnalignableReason, string>;

/**
 * Why each reason means the other run cannot be said to have LACKED the event.
 *
 * This is the whole point of the `unalignable` list and of ADR 0013 N7: absence
 * is never presented as "nothing happened", so each sentence says what is
 * unknown rather than what is missing.
 */
export const UNALIGNABLE_REASON_SENTENCES = {
  missing_turn_number:
    "This event carries no turn number, so there is no coordinate to look for it at in the other run.",
  outside_shared_script:
    "The other run never received this turn, so it had no occasion to produce this event.",
  counterpart_capture_gap:
    "The other run's capture is incomplete or still open across this turn, so whether it produced this event was not recorded.",
} as const satisfies Record<PreviewRunUnalignableReason, string>;

// ── The recorded facts a pair is compared on ─────────────────────────────────

/**
 * Every member of the contract's closed field vocabulary, as a clinician reads
 * it. Closed on purpose, and `satisfies` holds this table to it: a field the
 * service adds fails the build here rather than rendering as a raw enum member.
 */
export const FIELD_LABELS = {
  phase: "Phase",
  track: "Track",
  step: "Step",
  status: "Event status",
  visibility: "Visibility",
  user_input: "Synthetic patient input",
  liv_response: "Liv's reply",
  position_before: "Position before",
  position_after: "Position after",
  system_prompt: "System prompt",
  messages: "Input messages",
  structured_result: "Structured result",
  patient_facing_output: "Patient-facing output",
  model: "Model",
  model_deployment: "Model deployment",
  invocation_outcome: "Model invocation",
  invocation_reason_code: "Invocation reason",
  composition_digest: "Prompt composition",
  segment_count: "Provenance segments",
  segments: "Segments",
  result: "Result",
  rationale_state: "Rationale",
  rationale: "Recorded rationale",
  score: "Score",
  observed_value: "Observed value",
  operator: "Operator",
  threshold: "Threshold",
  passed: "Criterion met",
  source_version: "Clinical source version",
  selected_choice: "Selected",
  considered_choices: "Considered",
  reason_code: "Reason",
  governing_config: "Governing configuration",
  disposition: "Disposition",
  unmet_criteria: "Unmet criteria",
  compared_score: "Compared score",
  outcome: "Outcome",
  detection: "Detection",
  intervention: "Intervention",
  clinical_effect: "Clinical effect",
  policy_identity: "Policy",
  transition_from: "Transition from",
  transition_to: "Transition to",
} as const satisfies Record<PreviewRunComparisonField, string>;

/**
 * The fields whose recorded value is a BODY, carried as a content hash rather
 * than as text.
 *
 * The comparison is body-free by construction, so two different digests on one
 * of these mean "the recorded text differs" and carry none of it. Every other
 * field carries the producer's own recorded value.
 */
export const BODY_FIELDS = [
  "system_prompt",
  "messages",
  "structured_result",
  "patient_facing_output",
  "user_input",
  "liv_response",
  "rationale",
] as const satisfies readonly PreviewRunComparisonField[];

export function isBodyField(field: PreviewRunComparisonField): boolean {
  return (BODY_FIELDS as readonly string[]).includes(field);
}

/**
 * The subset of those a PROMPT CALL carries — and therefore the only body
 * differences the on-demand reader can open.
 *
 * The distinction is not cosmetic. `ComparePromptBodies` reads each side
 * through the event-detail route and renders it with the prompt-call reader
 * (#1099), which accepts a `prompt_call` event and nothing else. A turn's
 * `user_input`/`liv_response` and a classification's `rationale` are bodies
 * too, and their events are NOT prompt calls: offering the control there would
 * spend two reads the Editor Service audits as "full prompt detail viewed" and
 * end on an unreadable-event state (#1128 review, finding 2). Those
 * differences are reported as the digests the comparison carries, and a
 * family-specific reader is a later leaf's.
 */
export const PROMPT_CALL_BODY_FIELDS = [
  "system_prompt",
  "messages",
  "structured_result",
  "patient_facing_output",
] as const satisfies readonly PreviewRunComparisonField[];

/** The one event family whose bodies this build can open. */
export const PROMPT_CALL_KIND: TraceEventKind = "prompt_call";

/**
 * Whether the on-demand prompt read can show anything for this pair: it is a
 * prompt-call pair, and a body the prompt reader renders is among what differs.
 */
export function promptBodiesLoadable(pair: PreviewRunAlignedEvent): boolean {
  return (
    pair.key.kind === PROMPT_CALL_KIND &&
    pair.differences.some((difference) =>
      (PROMPT_CALL_BODY_FIELDS as readonly string[]).includes(difference.field),
    )
  );
}

/**
 * Whether a pair's recorded text differs somewhere this build cannot open —
 * so the card can say that plainly instead of offering a control that would
 * fail, or saying nothing at all about a difference the digests already show.
 */
export function hasUnopenableBodyDifference(pair: PreviewRunAlignedEvent): boolean {
  return (
    !promptBodiesLoadable(pair) &&
    pair.differences.some((difference) => isBodyField(difference.field))
  );
}

export const UNOPENABLE_BODY_SENTENCE =
  "The recorded body differs. Its value on each side — a content hash, an omission reason or no recorded value — is shown above. Only a prompt call's exact text can be opened here.";

/**
 * The one free-text member the contract compares and never carries.
 *
 * `clinical_effect` arrives with both sides null BY DESIGN — it is compared
 * server-side and withheld — so a card must say that the fact differs and that
 * its value is deliberately not published, rather than rendering "not recorded"
 * twice and reading as though nothing was there.
 */
export const WITHHELD_FIELD: PreviewRunComparisonField = "clinical_effect";

export const WITHHELD_SENTENCE =
  "This is free text and is deliberately not carried in a comparison. The Editor Service compared it and reports only that it differs.";

/**
 * One recorded value as a clinician reads it.
 *
 * A compound member arrives as a compact JSON array so its encoding is exact —
 * `["agent","track","phase","step"]`, with `null` kept — which is precise and
 * unreadable. This unpacks THAT array and nothing else: a value that is not a
 * well-formed JSON array is shown verbatim, because a fallback that guessed
 * would silently rewrite a value the producer recorded.
 */
export function formatRecordedValue(
  value: string | number | boolean | readonly string[] | null,
): { text: string; absent: boolean } {
  if (value === null) {
    return { text: "not recorded", absent: true };
  }
  if (typeof value === "boolean") {
    return { text: value ? "Yes" : "No", absent: false };
  }
  if (typeof value === "number") {
    return { text: String(value), absent: false };
  }
  if (Array.isArray(value)) {
    return value.length === 0
      ? { text: "none", absent: true }
      : { text: value.map(unpackCompound).join(" · "), absent: false };
  }
  return { text: unpackCompound(value as string), absent: false };
}

/** `["conversation",null,"intake","q1"]` → `conversation · — · intake · q1`; anything else, verbatim. */
function unpackCompound(value: string): string {
  if (!value.startsWith("[")) {
    return value;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  if (!Array.isArray(parsed)) {
    return value;
  }
  return parsed
    .map((member) =>
      member === null
        ? "—"
        : typeof member === "string"
          ? member
          : JSON.stringify(member),
    )
    .join(" · ");
}

// ── Coordinates and counts, as sentences ─────────────────────────────────────

/**
 * The alignment key as a clinician reads it: the turn, the agent, and whichever
 * of the producer's own identifiers this family sets. Occurrence is stated only
 * when it is not the first, because "occurrence 1" on every single-occurrence
 * coordinate is noise.
 */
export function describeAlignmentKey(key: PreviewRunAlignmentKey): string {
  const parts = [`Turn ${key.turnNumber}`];
  if (key.agent !== null) {
    parts.push(key.agent);
  }
  for (const member of [key.callType, key.clinicalSource, key.alternative]) {
    if (member !== null && member !== undefined) {
      parts.push(unpackCompound(member));
    }
  }
  if (key.occurrence > 0) {
    parts.push(`occurrence ${key.occurrence + 1}`);
  }
  return parts.join(" · ");
}

/** `1 change` / `3 changes` — the plural rule these sentences all share. */
export function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/**
 * One sentence for a set of counts, built as a single string.
 *
 * One string rather than composed spans for the reason `PreviewRunDecisions`
 * states: a screen reader announces a live region's text, and a sentence split
 * across elements is announced as fragments.
 */
export function describeCounts(counts: {
  aligned: number;
  identical: number;
  differing: number;
  added: number;
  removed: number;
  unalignable: number;
}): string {
  const parts = [
    `${plural(counts.differing, "changed pair", "changed pairs")}`,
    `${plural(counts.identical, "identical pair", "identical pairs")}`,
  ];
  if (counts.added > 0) {
    parts.push(`${plural(counts.added, "event", "events")} only in the compared run`);
  }
  if (counts.removed > 0) {
    parts.push(`${plural(counts.removed, "event", "events")} only in the base run`);
  }
  if (counts.unalignable > 0) {
    parts.push(
      `${plural(counts.unalignable, "event", "events")} that cannot be aligned`,
    );
  }
  return `${parts.join(", ")}.`;
}

/** The whole comparison's roll-up, including the reordering the service measured. */
export function describeTotals(counts: PreviewRunComparisonCounts): string {
  const base = describeCounts(counts);
  return counts.reordered > 0
    ? `${base.slice(0, -1)}; ${plural(counts.reordered, "aligned pair", "aligned pairs")} occurred in a different order.`
    : base;
}
